import { useCallback } from 'react';
import type { AddressType } from '@sweetgreen/garnish';

import { useLastValidOrder } from '@order/Order';
import { useTelemetry } from '@order/Telemetry';

import { useNavigateToMenu } from '../../hooks/useNavigateToMenu';
import { useHomeNavigation } from './HomeScreen.navigation';

export type HomeScreenHeroCtaAction = 'view_menu';

export const useHomeScreenHeroCTAHandler = (
  action?: HomeScreenHeroCtaAction,
) => {
  const handleViewMenu = useHomeScreenViewMenuHeroCTA();

  if (action === 'view_menu') {
    return handleViewMenu;
  }

  //... handle other actions

  // default handler
  return handleViewMenu;
};

export const useHomeScreenViewMenuHeroCTA = () => {
  const lastValidOrder = useLastValidOrder();
  const navigateToMenu = useNavigateToMenu();
  const { handleGoToLocations } = useHomeNavigation();
  const lastOrderRestaurantSlug = lastValidOrder?.restaurant.slug;

  const lastOrderType = orderTypeFromOrder(lastValidOrder);

  // ────────────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  return useCallback(() => {
    track('home.hero_module');

    if (lastOrderRestaurantSlug) {
      navigateToMenu({
        orderType: 'pickup',
        restaurantSlug: lastOrderRestaurantSlug,
      });

      return;
    }

    handleGoToLocations(lastOrderType);
  }, [
    lastOrderRestaurantSlug,
    lastOrderType,
    navigateToMenu,
    handleGoToLocations,
    track,
  ]);
};

const orderTypeFromOrder = (order?: Order): AddressType => {
  if (!order) return 'pickup';

  if (order.restaurant.isOutpost) return 'outpost';

  return order.orderType.toLowerCase() as AddressType;
};

type Order = NonNullable<ReturnType<typeof useLastValidOrder>>;
